import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Box, Button, LinearProgress, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import { useOnboarding } from "../../../contexts/OnboardingContext";
import { ONBOARDING_STEPS } from "../../../utils/constants";

const ScanCompleteStep = () => {
  const colors = useColors();
  const navigate = useNavigate();
  const { setOnboardingStage, closeOnboardingModal } = useOnboarding();
  const { trackEvent } = useAPI();

  const handleViewControls = () => {
    trackEvent("User clicked view controls and gaps");
    navigate("/posture/controls");
    closeOnboardingModal();
  };

  const handleNext = () => {
    trackEvent("User moved to learn basics step");
    setOnboardingStage(ONBOARDING_STEPS.LEARN_BASICS);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        p: 4,
        height: "100%",
      }}
    >
      <Typography variant="body2" sx={{ mb: 2 }}>
        Step 2 of 4
      </Typography>

      <Typography variant="h4" sx={{ fontWeight: "bold", mb: 4 }}>
        Scan Complete
      </Typography>

      <Box sx={{ width: "100%", mb: 4 }}>
        <LinearProgress
          variant="determinate"
          value={100}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: `${colors.primary_main}20`,
            "& .MuiLinearProgress-bar": {
              backgroundColor: colors.primary_main,
            },
          }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Scan complete
        </Typography>
      </Box>

      <Box
        sx={{
          border: `1px solid ${colors.divider}`,
          borderRadius: 2,
          p: 3,
          mb: 4,
        }}
      >
        <Box sx={{ display: "flex", mb: 2, alignItems: "center" }}>
          <Box
            sx={{
              mr: 2,
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              backgroundColor: `${colors.primary_main}20`,
              color: colors.primary_main,
            }}
          >
            <CheckCircleOutlineIcon />
          </Box>
          <Typography variant="h6" fontWeight="medium">
            Scan complete – insights are ready
          </Typography>
        </Box>

        <Typography variant="body1" sx={{ mb: 3 }}>
          We've analyzed your environment and surfaced the most important gaps
          from your EDR. Dive into each control's findings, Breeze analysis, and
          what it means for your environment.
        </Typography>

        <Button
          variant="contained"
          onClick={handleViewControls}
          sx={{
            py: 1.5,
            px: 3,
            borderRadius: "8px",
            textTransform: "none",
          }}
        >
          View Controls & Gaps
        </Button>
      </Box>

      <Box sx={{ mt: "auto" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            color: colors.warning_main,
            p: 2,
          }}
        >
          <span
            role="img"
            aria-label="light bulb"
            style={{ fontSize: "24px", marginRight: "8px" }}
          >
            💡
          </span>
          <Typography variant="body2" sx={{ fontStyle: "italic" }}>
            In 2004, the FBI lost 160 laptops. None were encrypted. Good times.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button
          variant="contained"
          onClick={handleNext}
          sx={{
            py: 1.5,
            px: 3,
            borderRadius: "8px",
            textTransform: "none",
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default ScanCompleteStep;
